import React, { memo, useMemo } from 'react';

import { useInterpret } from '@xstate/react';
import { useActorRef } from '@xstate5/react';

import { networkMonitorMachine } from '@sg/garnish';
import { type otaMachine } from '@sg/ota-installer';

import {
  postPurchaseModalMachine,
  useInitLoyaltyOffersModalMachine,
} from '@order/features/loyalty';
import { useDynamicFeatureFlags } from '@order/LaunchDarkly';

import { cartMachine } from '../../hooks/useCart/Cart.machine/Cart.machine';
import { reorderMachine } from '../../screens/ReorderingScreen/machines/Reorder.machine';
import { useAzureAuthService } from '../AzureAuth';
import { useInitializeDietaryPreferencesActor } from '../DietaryPreferences';
import {
  EssentialMachinesContext,
  EssentialMachinesProvider,
  useEssentialMachines,
} from '../EssentialMachines';
import { useInitLastInteractedStoreMachine } from '../LastInteractedStore/machine';
import { placedOrdersCounterMachine } from '../PlacedOrdersCounter/PlacedOrdersCounter.machine';

import type { AuthMachineRefType } from '../AuthMachine/useInitAuthMachine';
import type { LastInteractedStoreMachineRefType } from '../LastInteractedStore/machine';
import type { ActorRefFrom } from 'xstate';
import type { ActorRefFrom as ActorRefFromV5 } from 'xstate5';

//
// ─── CONTEXT SETUP ──────────────────────────────────────────────────────────────
//

const INITIAL_CONTEXT_VALUE = undefined;

const GlobalAppStateContext = React.createContext<ContextValue>(INITIAL_CONTEXT_VALUE);

// TYPES
type ContextValue = typeof INITIAL_CONTEXT_VALUE | GlobalAppStateContextType;

type GlobalAppStateContextType = Readonly<{
  authMachineRef: AuthMachineRefType;
  loyaltyOffersModalMachineRef: ReturnType<typeof useInitLoyaltyOffersModalMachine>;
  lastInteractedStoreMachineRef: LastInteractedStoreMachineRefType;
  postPurchaseModalMachineRef: ActorRefFromV5<typeof postPurchaseModalMachine>;
  otaMachineRef: ActorRefFromV5<typeof otaMachine>;
  cartMachineRef: ActorRefFrom<typeof cartMachine>;
  networkMonitorMachineRef: ActorRefFrom<typeof networkMonitorMachine>;
  reorderMachineRef: ActorRefFrom<typeof reorderMachine>;
  placedOrderCounterMachineRef: ActorRefFrom<typeof placedOrdersCounterMachine>;

  dietaryRestrictionsMachineRef: ReturnType<typeof useInitializeDietaryPreferencesActor>;
  azureAuthMachineRef: ReturnType<typeof useAzureAuthService>;
  dynamicFeatureFlags: ReturnType<typeof useDynamicFeatureFlags>;
}>;

//
// ─── PROVIDER ────────────────────────────────────────────────────────────────
//

const useInterpretedMachineRefs = () => {
  const { authMachineRef, otaMachineRef } = useEssentialMachines();

  return {
    authMachineRef,
    otaMachineRef,
    lastInteractedStoreMachineRef: useInitLastInteractedStoreMachine(),
    dietaryRestrictionsMachineRef: useInitializeDietaryPreferencesActor({ authMachineRef }),
    loyaltyOffersModalMachineRef: useInitLoyaltyOffersModalMachine(),
    cartMachineRef: useInterpret(cartMachine),
    networkMonitorMachineRef: useInterpret(networkMonitorMachine),
    reorderMachineRef: useInterpret(reorderMachine),
    placedOrderCounterMachineRef: useInterpret(placedOrdersCounterMachine),
    azureAuthMachineRef: useAzureAuthService(),
    postPurchaseModalMachineRef: useActorRef(postPurchaseModalMachine),
    dynamicFeatureFlags: useDynamicFeatureFlags(),
  };
};

export const GlobalAppState = memo((props: GlobalAppStateProps) => {
  // Allow `<GlobalAppState />` to be used standalone (e.g. in test wrappers)
  // by spawning the essential machines locally when no `<EssentialMachinesProvider />`
  // is mounted upstream.
  const essentialMachines = React.useContext(EssentialMachinesContext);

  if (!essentialMachines) {
    return (
      <EssentialMachinesProvider>
        <GlobalAppStateInner>{props.children}</GlobalAppStateInner>
      </EssentialMachinesProvider>
    );
  }

  return <GlobalAppStateInner>{props.children}</GlobalAppStateInner>;
});

const GlobalAppStateInner = memo((props: GlobalAppStateProps) => {
  const machineRefs = useInterpretedMachineRefs();
  const contextValue = useMemo(() => machineRefs, [machineRefs]);

  return (
    <GlobalAppStateContext.Provider value={contextValue}>
      {props.children}
    </GlobalAppStateContext.Provider>
  );
});

type GlobalAppStateProps = Readonly<{
  children: React.ReactNode;
}>;

//
// ─── HOOK ────────────────────────────────────────────────────────────────────
//

/**
 * CAUTION: Prefer more targeted hooks.
 * @see {@link: https://twitter.com/kentcdodds/status/1121440768277663745?lang=en}
 * >I never expose my context object to other modules, instead I create a custom
 *  Provider component and a custom hook consumer and expose those.
 *
 * NOTE: This is only intended to be used by other useContext-derived hooks eg useBagMachine
 */
export const useGlobalAppState = () => {
  const context = React.useContext(GlobalAppStateContext);

  if (!context) {
    throw new Error('cannot be rendered outside the <GlobalAppState />');
  }

  return context;
};
